import i18n from 'resources/locales/i18n'
import { StatusTypes } from '@grin-rnd/grin-api-sdk/dist/Enums/Statuses'
import { OTHER_TREATMENT_TYPE } from 'consts/treatmentConsts'

export const StatusTypesOrder = {
  invited: 1,
  'records-only': 2,
  'new-lead': 3,
  observation: 4,
  'active-treatment': 5,
  retention: 6,
  archived: 7
}

export const ScanFrequencyOptions = {
  'active-treatment': [7, 14, 21, 28],
  observation: [3, 6],
  retention: [3, 6]
}

export const scanFrequencyCustomOption = {
  label: i18n.t('dialogs.scanFrequency.custom'),
  value: 'custom'
}

const DEFAULT_SCAN_FREQUENCY_STATUS_TYPE = 'active-treatment'
// Keep this paired with the active-treatment fallback options above.
const DEFAULT_SCAN_FREQUENCY_PERIOD_TYPE = 'day'

export const getScanFrequencyOptionsForStatus = ({ areScanSettingsDisabled, postConfirmationStatus, status }) => {
  const statusType = areScanSettingsDisabled
    ? postConfirmationStatus?.type || DEFAULT_SCAN_FREQUENCY_STATUS_TYPE
    : status?.type

  return ScanFrequencyOptions[statusType] || ScanFrequencyOptions[DEFAULT_SCAN_FREQUENCY_STATUS_TYPE]
}

export const buildScanFrequencyOptions = ({ areScanSettingsDisabled, postConfirmationStatus, status, t }) => {
  const options = getScanFrequencyOptionsForStatus({ areScanSettingsDisabled, postConfirmationStatus, status })
  const scanFrequencyDefaultsSource = areScanSettingsDisabled ? postConfirmationStatus : status
  const statusScanDefaults = JSON.parse(scanFrequencyDefaultsSource?.scanFrequencyDefaults || '{}')
  const periodType = statusScanDefaults.periodType || DEFAULT_SCAN_FREQUENCY_PERIOD_TYPE

  return {
    statusScanDefaults,
    frequencyOptions: options
      .map(option => ({
        label: option.toString().concat(' ' + t(`periodTypes.${periodType}`)),
        value: option
      }))
      .concat(scanFrequencyCustomOption)
  }
}

export const DEFAULT_PERIOD_TYPE_OPTIONS = ['day', 'month']
const periodTypeOptionsByStatus = {
  'active-treatment_rm': ['day'],
  'active-treatment_rc': ['day'],
  'active-treatment_ortho': ['day']
}

export const getPeriodTypeOptions = statusKey =>
  (periodTypeOptionsByStatus[statusKey] || DEFAULT_PERIOD_TYPE_OPTIONS).map(periodType => ({
    label: i18n.t(`periodTypes.${periodType}`),
    value: periodType
  }))

const getStatusTypeFallbackFromKey = statusKey => {
  if (!statusKey || typeof statusKey !== 'string') {
    return undefined
  }

  const separatorIndex = statusKey.lastIndexOf('_')
  return separatorIndex === -1 ? statusKey : statusKey.slice(0, separatorIndex)
}

export const getStatusTypeByKey = ({ statusesByProgram = {}, program, statusKey } = {}) => {
  if (!statusKey) {
    return undefined
  }

  const programStatuses = program ? statusesByProgram?.[program] : null
  const allStatuses = Object.values(statusesByProgram || {}).reduce(
    (result, statuses) => [...result, ...(statuses || [])],
    []
  )

  return (
    programStatuses?.find(status => status.key === statusKey)?.type ||
    allStatuses.find(status => status.key === statusKey)?.type ||
    getStatusTypeFallbackFromKey(statusKey)
  )
}

export const isPatientActive = patient => {
  if (!patient) {
    return false
  }

  const status = patient?.treatments?.items[0]?.currentStatus?.status
  return status?.scanCyclesEnabled
}

/**
 * Whether a treatment type / status pair is one where double scan is a per-patient setting.
 *
 * Double scan used to be decided globally by `Status.requireDoubleScan` (true only on
 * active-treatment) combined with a hardcoded Aligners check. That gave practices no way
 * to request a double scan for patients wearing a retainer or elastic bands, so it is now
 * a per-patient setting for the "Other" treatment type and for any patient in Retention -
 * Retention regardless of treatment type, since a patient in Retention wears an appliance
 * whatever they were treated with.
 */
const isDoubleScanSettingEligible = ({ treatmentType, statusType } = {}) =>
  treatmentType === OTHER_TREATMENT_TYPE || statusType === StatusTypes.Retention

/**
 * Whether the practice should be offered a single/double scan choice.
 *
 * Every picker (invite, patient card, status modal) goes through this, so the rule lives in
 * one place. Never for Archived: an archived patient takes no scans, so there is nothing to
 * choose, and an archived "Other" patient would otherwise get an editable control.
 * Mirrored by `offersDoubleScanChoice` in grin-native.
 */
export const shouldOfferDoubleScanChoice = ({ treatmentType, statusType } = {}) =>
  statusType !== StatusTypes.Archived && isDoubleScanSettingEligible({ treatmentType, statusType })

/**
 * Whether a patient's scans should be described with generalised "device/appliance"
 * wording rather than naming aligners, in both copy and iconography.
 *
 * True exactly when the double scan came from the per-patient setting, which covers the
 * "Other" treatment type and anyone in Retention -- including an Aligners patient a
 * practice moved to Retention, who is wearing a retainer rather than aligners.
 *
 * Deliberately not gated on Archived like the picker is: archiving does not change what
 * an "Other" patient's past scans were taken with, so their feed keeps the same wording.
 *
 * Mirrors `shouldUseGenericApplianceCopy` in grin-native so the doctor portal and the
 * patient app can never describe the same scan differently. The eligibility guard matters
 * because the flag survives status changes.
 */
export const shouldUseGenericApplianceCopy = treatment =>
  treatment?.doubleScanEnabled === true &&
  isDoubleScanSettingEligible({
    treatmentType: treatment?.type,
    statusType: treatment?.currentStatus?.status?.type
  })

/**
 * Status type from a status key, tolerating a missing or malformed key.
 *
 * The keys are `<type>_<program>`; `decodeStatusKey` throws on anything else, and these
 * call sites run during render, where a throw white-screens the invite modal.
 */
export const getStatusTypeFromKey = statusKey => statusKey?.split?.('_')?.[0]

/**
 * A cached treatment with a double-scan choice that was just saved copied onto it.
 *
 * The status endpoint answers with the new TreatmentStatus only, so without this every
 * cached copy of the treatment keeps the old flag after the status modal saves one. The
 * next status modal or Scan Settings save then seeds from that stale value and writes it
 * back explicitly, undoing the practice's choice. Returns the same object when there is
 * nothing to apply (other treatment, or the key was omitted), so reducers can skip work.
 */
export const withSavedDoubleScanSetting = (treatment, { treatmentId, doubleScanEnabled } = {}) =>
  treatment?.id && treatment.id === treatmentId && typeof doubleScanEnabled === 'boolean'
    ? { ...treatment, doubleScanEnabled }
    : treatment

/** `withSavedDoubleScanSetting` applied to a patient's current (first) treatment. */
export const withPatientDoubleScanSetting = (patient, saved) => {
  const items = patient?.treatments?.items
  const updated = withSavedDoubleScanSetting(items?.[0], saved)

  return updated === items?.[0]
    ? patient
    : { ...patient, treatments: { ...patient.treatments, items: [updated, ...items.slice(1)] } }
}

/**
 * What a save should send for `doubleScanEnabled`, as a spreadable object.
 *
 * Three surfaces decide this - the Scan Settings tab, the status modal and the invite flow - and
 * each used to re-implement it. The three cases are not symmetrical:
 *
 * - the control is offered: send what the practice chose.
 * - it is hidden and nothing about the treatment type moved: send nothing. Omitting preserves,
 *   and these forms hold a copy of the treatment that a status change does not refresh, so an
 *   explicit false would let an unrelated save wipe a choice made somewhere else.
 * - it is hidden *because this same save moves the patient off an eligible type*: send false.
 *   Both platforms re-check eligibility, so a leftover true is inert while the patient stays
 *   ineligible - but it survives in storage, and the day an automation moves them to Retention
 *   the app starts asking for two videos nobody prescribed. This is the only moment we know the
 *   value became meaningless.
 */
export const doubleScanPayload = ({ treatmentType, statusType, isDoubleScan, previousTreatmentType }) => {
  if (shouldOfferDoubleScanChoice({ treatmentType, statusType })) {
    return { doubleScanEnabled: !!isDoubleScan }
  }

  const movedOffItsType = previousTreatmentType !== undefined && treatmentType !== previousTreatmentType

  return movedOffItsType ? { doubleScanEnabled: false } : {}
}
